import { mensajeDeSeleccion } from "../../../helpers/beautyLineas";
import { seriesDateLabel, shortSeriesDate, weekdayName } from "../../reservas/components/courtSeriesModel";

/* Piezas puras del "Turno fijo" de Turnos Beauty. Las reglas de fechas (26 semanas, 8 por defecto) y las etiquetas
   de estado son las de Canchas (courtSeriesModel); acá solo vive lo propio de Beauty: el intervalo en semanas,
   el profesional y los textos de la serie. */

export const INTERVAL_OPTIONS = [
  { value: 1, label: "Todas las semanas" },
  { value: 2, label: "2 semanas" },
  { value: 3, label: "3 semanas" },
  { value: 4, label: "4 semanas" },
];

// A series without interval (older ones) repeats every week.
const semanas = (weeks) => Number(weeks) || 1;

/** "Todas las semanas" / "2 semanas": el valor del selector "Cada". */
export const intervalLabel = (weeks) => (semanas(weeks) === 1 ? "Todas las semanas" : `${semanas(weeks)} semanas`);

/** "todas las semanas" / "cada 2 semanas": dentro de una frase. */
export const intervalPhrase = (weeks) => (semanas(weeks) === 1 ? "todas las semanas" : `cada ${semanas(weeks)} semanas`);

/** "Todas las semanas" / "Cada 2 semanas": al comienzo de una línea. */
export const intervalHeading = (weeks) => (semanas(weeks) === 1 ? "Todas las semanas" : `Cada ${semanas(weeks)} semanas`);

/** "los jueves": el plural del día de la semana (0 = domingo). */
export const losDias = (weekday) => {
  const name = weekdayName(weekday).toLowerCase();
  if (!name) return "";
  return `los ${name.endsWith("o") ? `${name}s` : name}`;
};

const lowerDate = (iso) => seriesDateLabel(iso).toLowerCase();

/** Hint under the "Cada" / "Hasta" fields. */
export const seriesHint = ({ intervalWeeks, weekday, slotStart, professionalName, maxWeeks }) => {
  const when = `${losDias(weekday)}${slotStart ? ` a las ${slotStart}` : ""}`;
  const who = professionalName ? ` con ${professionalName}` : "";
  return `Se repite ${intervalPhrase(intervalWeeks)}, ${when}${who}, hasta un máximo de ${maxWeeks} semanas.`;
};

/* The back answers `state` per date (taken, not reserved); the courts' list wants { available, reason }. */
const reasonOf = (state) => (state === "taken" ? "reserved" : state);

/** Preview dates for CourtSeriesDateList. */
export const previewEntries = (dates) =>
  (Array.isArray(dates) ? dates : []).map((d) => ({ date: d?.date, available: d?.state === "available", reason: reasonOf(d?.state) }));

/** Skipped dates (create response / 409) for CourtSeriesDateList: `reason` uses the preview states. */
export const skippedEntries = (skipped) =>
  (Array.isArray(skipped) ? skipped : []).map((d) => ({ date: d?.date, available: false, reason: reasonOf(d?.reason) }));

const ERROR_MESSAGES = {
  professional_required: "Un turno fijo necesita un profesional. Elegí uno en lugar de “Sin preferencia”.",
  invalid_professional: "Ese profesional no está disponible para el turno fijo.",
  invalid_date: "La fecha no es válida.",
  invalid_date_range: "La fecha final no puede ser anterior a la primera.",
  series_too_long: "Un turno fijo dura como máximo 26 semanas.",
  invalid_time: "El horario no es válido.",
  invalid_interval: "Elegí si se repite cada 1, 2, 3 o 4 semanas.",
  weekday_mismatch: "El día de la semana no coincide con la fecha elegida.",
  name_required: "Falta el nombre de la persona.",
  phone_required: "El teléfono es obligatorio para un turno fijo.",
  invalid_email: "El correo no es válido.",
  no_dates_available: "Ninguna de las fechas está disponible.",
  series_cancelled: "Este turno fijo ya estaba cancelado.",
};

/** Spanish text for a failed series call: known `codigo`, then the selection codes, then the back's message. */
export const seriesErrorMessage = (error, fallback) =>
  ERROR_MESSAGES[error?.response?.data?.codigo] || mensajeDeSeleccion(error) || error?.response?.data?.message || fallback;

/** "Cada 2 semanas, los jueves a las 09:00, hasta el 14/01/2027": the row of the appointment detail. */
export const serieDescripcion = (serie) =>
  `${intervalHeading(serie?.intervalWeeks)}, ${losDias(serie?.weekday)} a las ${serie?.slotStart}, hasta el ${shortSeriesDate(serie?.dateTo)}`;

/** "Turno 1 de 7 · Próximo: jueves 05/11" (no "Próximo" when none is left). */
export const serieProgreso = (serie) => {
  const base = `Turno ${serie?.position} de ${serie?.total}`;
  return serie?.nextDate ? `${base} · Próximo: ${lowerDate(serie.nextDate)}` : base;
};

/** "Sin fechas próximas" / "6 fechas próximas · Próxima: Jueves 22/10": list line. */
export const proximasLabel = (serie) => {
  const count = Number(serie?.futureCount) || 0;
  if (count === 0 || !serie?.nextDate) return "Sin fechas próximas";
  return `${count} ${count === 1 ? "fecha próxima" : "fechas próximas"} · Próxima: ${seriesDateLabel(serie.nextDate)}`;
};

/** Reasons the back keeps a date when cancelling a series (Beauty says "ingreso", not "check-in"). */
export const KEPT_LABELS = { charged: "ya cobrado", checked_in: "con ingreso" };

/** Toast after cancelling this and the following dates. */
export const cancelacionMensaje = (result) => {
  const n = Array.isArray(result?.cancelled) ? result.cancelled.length : 0;
  return n > 0 ? `Se cancelaron ${n} ${n === 1 ? "turno" : "turnos"} del turno fijo.` : "No había turnos para cancelar.";
};

export const tieneSerie = (turno) => Boolean(turno?.series?.id);
